import { useCallback, useMemo } from 'react';
import { InitialConfigType } from '@lexical/react/LexicalComposer';
import {
  $convertFromMarkdownString,
  $convertToMarkdownString
} from '@lexical/markdown';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { EditorState, LexicalEditor, LineBreakNode } from 'lexical';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';

import { ClassName, IsDisabled } from '../../../../types';
import { ProjectNanoID } from '../../../../main/projects/projectsTypes';
import { TaskNanoID } from '../../../../main/tasks/tasksTypes';

import {
  LexicalFieldValueType,
  OnChangeLexicalFieldValue
} from '../../../FormFields/LexicalEditorField/LexicalEditorField';

import { useCacheTaskMembers } from '../../../../main/taskMembers/hooks/useCacheTaskMembers';
import {
  FETCH_CACHE_TASK_MEMBERS_QUERY,
  CacheFetchTaskMembersQueryResponse
} from '../../../../main/taskMembers/queries/fetchCacheTaskMembers.query';
import {
  FETCH_PROJECT_MEMBERS_QUERY,
  FetchProjectMembersQueryResponse
} from '../../../../main/projectMembers/queries/projectMembers.query';

import { useProjectMembers } from '../../../../main/projectMembers/hooks/useProjectMembers';

import { useProjectContext } from '../../../../main/projects/hooks/useProjectContext';
import { useTaskContext } from '../../../../main/tasks/hooks/useTaskContext';

import { MentionNode } from '../../utils/MentionNode';
import { getMentionIds } from '../../utils/getMentionIds';
import {
  LEXICAL_EDITOR_TRANSFORMERS,
  getMentionIdsFromMarkdown
} from '../../utils/mentionMarkdownTransformer';

import { ProjectCache } from '../../../../main/projects/ProjectCache';
import { TaskCache } from '../../../../main/tasks/TaskCache';

const LexicalEditorNodes = [
  AutoLinkNode,
  CodeNode,
  CodeHighlightNode,
  HeadingNode,
  LinkNode,
  ListNode,
  ListItemNode,
  MentionNode,
  QuoteNode,
  LineBreakNode
];

const lexicalEditorInitialConfig = (
  root: ClassName = 'markdown markdown-sm p-2 bg-gray-200 dark:bg-gray-800 text-sm rounded-md min-h-[2rem] focus:ring-4 outline-none h-full overflow-y-scroll'
) => ({
  namespace: 'lexical-editor',
  nodes: LexicalEditorNodes,
  theme: {
    // root: 'markdown markdown-sm sm:markdown-md markdown-pre:whitespace-pre-wrap overflow-y-scroll w-full max-h-500 pr-2 pl-4 py-2.5 bg-gray-200 dark:bg-gray-800 text-sm leading-snug outline-none border-none resize-none',
    root,
    link: 'cursor-pointer',
    code: 'bg-inherit text-inherit before:content-[""] after:content-[""]',
    text: {
      bold: 'font-semibold',
      underline: 'underline',
      italic: 'italic',
      strikethrough: 'line-through',
      underlineStrikethrough: 'underlined-line-through'
    },
    list: {
      ul: '*:my-0',
      ol: '*:my-0'
    }
  },
  onError: (error: Error) => {
    console.log(error);
  }
});

interface LexicalEditorHelperOptions {
  disabled?: IsDisabled;
  onChange?: OnChangeLexicalFieldValue;
  value: LexicalFieldValueType;
  inputClassName?: ClassName;
  projectNanoId?: ProjectNanoID;
  taskNanoId?: TaskNanoID;
}
function useLexicalEditorHelper({
  disabled,
  onChange,
  value,
  inputClassName,
  projectNanoId: propsProjectNanoId,
  taskNanoId: propsTaskNanoId
}: LexicalEditorHelperOptions) {
  const { projectNanoId: projectContextProjectNanoId } = useProjectContext();

  const {
    taskProjectNanoId: taskContextProjectNanoId,
    taskNanoId: taskNanoIdFromContext
  } = useTaskContext();

  const projectNanoId =
    propsProjectNanoId ||
    taskContextProjectNanoId ||
    projectContextProjectNanoId;

  const taskNanoId = propsTaskNanoId || taskNanoIdFromContext;

  const { taskMembers } =
    useCacheTaskMembers<CacheFetchTaskMembersQueryResponse>({
      cacheKey: TaskCache.taskMembersCacheKey(taskNanoId),
      query: FETCH_CACHE_TASK_MEMBERS_QUERY,
      initialFilters: {
        taskNanoId: {
          eq: taskNanoId
        }
      },
      initialLimit: 1000,
      options: {
        enabled: !!taskNanoId,
        enabledPlaceholder: !!taskNanoId,
        withoutPrefetch: true
      }
    });

  const { projectMembers } =
    useProjectMembers<FetchProjectMembersQueryResponse>({
      cacheKey: ProjectCache.projectMembersCacheKey(projectNanoId),
      query: FETCH_PROJECT_MEMBERS_QUERY,
      initialFilters: { projectNanoId },
      initialLimit: 1000,
      options: {
        enabled: !!projectNanoId,
        enabledPlaceholder: !!projectNanoId,
        withoutPrefetch: true
      }
    });

  const lexicalEditorConfig = useMemo<InitialConfigType>(
    () => ({
      ...lexicalEditorInitialConfig(inputClassName),
      editorState: () => {
        $convertFromMarkdownString(value?.text, LEXICAL_EDITOR_TRANSFORMERS);
      },
      editable: !disabled
    }),
    [disabled, inputClassName, value?.text]
  );

  const handleChange = useCallback<
    (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => void
  >(
    (editorState) => {
      editorState.read(() => {
        const mentionNodeIds = [...editorState._nodeMap.values()]
          .filter((node): node is MentionNode => node.__type === 'mention')
          .map((node) => node.__id);

        const markdown =
          $convertToMarkdownString(LEXICAL_EDITOR_TRANSFORMERS) || undefined;

        const mentionIds = getMentionIds({
          mentionNodeIds: [
            ...mentionNodeIds,
            ...getMentionIdsFromMarkdown(markdown)
          ],
          projectMembers,
          taskMembers
        });

        const newValue =
          markdown || mentionIds ? { text: markdown, mentionIds } : undefined;

        onChange?.(newValue);
      });
    },
    [onChange, projectMembers, taskMembers]
  );
  return {
    lexicalEditorConfig,
    handleChange
  };
}

export default useLexicalEditorHelper;
